/**
 * Screen Wake Lock, and the several ways it is allowed to not happen.
 *
 * This is progressive enhancement and nothing else. `navigator.wakeLock` is
 * absent on older iOS Safari, the request is rejected outright when the page is
 * not visible, and the browser drops an acquired lock on its own the moment the
 * page hides — without telling the caller through the path it was requested on.
 * Every one of those is an ordinary outcome, so nothing here rejects: a throw on
 * this path would be a throw on the path that starts a round, and the wake lock
 * must never block training.
 *
 * `isHeld()` is the only way a caller learns the screen is no longer being kept
 * awake, which is why the sentinel's `release` event is subscribed to rather
 * than assumed away. It is also why the lock is re-requested on every return to
 * `"visible"` (`docs/02-ARCHITECTURE.md` §5.5) instead of once per session.
 */

import type { WakeLockOutcome, WakeLockPort } from "@ff/domain/ports";

export interface WakeLockSentinelLike {
  /** The browser sets this when it drops the lock, including on its own. */
  readonly released: boolean;
  release(): Promise<void>;
  addEventListener(type: "release", listener: (event: unknown) => void): void;
  removeEventListener(type: "release", listener: (event: unknown) => void): void;
}

export interface WakeLockLike {
  request(type: "screen"): Promise<WakeLockSentinelLike>;
}

export interface WakeLockEnvironment {
  /**
   * Resolved per request rather than captured once. The capability is not
   * stable for the life of a document — a page moved into a background tab is
   * refused, and some engines expose the API only from a secure context that a
   * navigation can leave.
   */
  wakeLock(): WakeLockLike | undefined;
}

export function browserWakeLockEnvironment(): WakeLockEnvironment {
  return {
    wakeLock: () => {
      const candidate = (globalThis.navigator as { wakeLock?: unknown } | undefined)?.wakeLock;
      if (candidate === undefined || candidate === null) return undefined;
      const request = (candidate as { request?: unknown }).request;
      return typeof request === "function" ? (candidate as WakeLockLike) : undefined;
    },
  };
}

interface HeldLock {
  readonly sentinel: WakeLockSentinelLike;
  readonly onRelease: (event: unknown) => void;
}

export class BrowserWakeLock implements WakeLockPort {
  readonly #environment: WakeLockEnvironment;
  #held: HeldLock | undefined;
  #pending: Promise<WakeLockOutcome> | undefined;
  /**
   * Bumped by every {@link release}. An acquisition that resolves after the
   * caller has asked to stop belongs to nobody, and keeping it would leave the
   * screen awake for a session that already ended — the exact failure a stacked
   * sentinel produces, arriving by a different route.
   */
  #generation = 0;

  constructor(environment: WakeLockEnvironment = browserWakeLockEnvironment()) {
    this.#environment = environment;
  }

  async request(): Promise<WakeLockOutcome> {
    if (this.isHeld()) return "acquired";
    // Two requests in flight would produce two sentinels, and the single
    // `release` the caller makes would undo only one of them.
    if (this.#pending !== undefined) return this.#pending;

    const source = this.#environment.wakeLock();
    if (source === undefined) return "unsupported";

    const pending = this.#acquire(source, this.#generation);
    this.#pending = pending;
    try {
      return await pending;
    } finally {
      if (this.#pending === pending) this.#pending = undefined;
    }
  }

  async release(): Promise<void> {
    this.#generation += 1;
    const held = this.#held;
    this.#held = undefined;
    if (held === undefined) return;
    await releaseSentinel(held);
  }

  isHeld(): boolean {
    const held = this.#held;
    if (held === undefined) return false;
    // `released` covers a browser that drops the lock without dispatching the
    // event; nothing may claim the screen is being kept awake when it is not.
    return !held.sentinel.released;
  }

  async #acquire(source: WakeLockLike, generation: number): Promise<WakeLockOutcome> {
    let sentinel: WakeLockSentinelLike;
    try {
      sentinel = await source.request("screen");
    } catch {
      // A rejection here is a refusal, not a fault: the page was not visible, or
      // the user agent declined. Either way nothing is held and the caller has a
      // value it can act on.
      return "denied";
    }

    const held: HeldLock = {
      sentinel,
      onRelease: () => {
        if (this.#held === held) this.#held = undefined;
      },
    };
    sentinel.addEventListener("release", held.onRelease);

    if (generation !== this.#generation) {
      // Released while this request was in flight. Hand the sentinel straight
      // back rather than storing it.
      await releaseSentinel(held);
      return "denied";
    }

    this.#held = held;
    return "acquired";
  }
}

async function releaseSentinel(held: HeldLock): Promise<void> {
  held.sentinel.removeEventListener("release", held.onRelease);
  try {
    await held.sentinel.release();
  } catch {
    // Releasing a lock the browser already dropped rejects in some engines.
    // There is nothing left to undo, and the port promises never to reject.
  }
}
